import React, { useEffect, useState } from "react";
import { Control, FieldValues, Path, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "@monorepo/components";
import { Box } from "@mui/material";

type TCounterInputProps<T extends FieldValues> = {
  defaultValue?: string;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  registerName?: string;
  responseError?: string;
  control: Control<T>;
  isCalculateLoading?: boolean;
  hideCurrency?: boolean;
  isFloat?: boolean;
  tooltipMessage?: string;
};

// for dispalying
const formatCounterView = (value: string) => {
  return value;
};
// for sending to backend
const formatCounterModel = (value: any) => {
  return value.replace(/,/, ".");
};

const counterRegex = /^([-0-9])*$/;
const floatCounterRegex = /^([-0-9]*(\.|,)?[0-9]*)?$/;

export function CounterInput<T extends FieldValues>({
  defaultValue,
  label,
  disabled,
  readOnly,
  registerName,
  responseError,
  control,
  isCalculateLoading,
  isFloat,
  tooltipMessage,
}: TCounterInputProps<T>) {
  const { setError, setValue, register } = useFormContext<T>();
  const [fieldName] = useState<string>(registerName ? registerName : "counter");
  const [counterViewValue, setCounterViewValue] = useState(defaultValue || formatCounterView("0.00"));
  const { t } = useTranslation();
  const {
    field: { onBlur, name, ...fieldProps },
    fieldState: { error },
  } = useController({
    name: fieldName as Path<T>,
    control,
    rules: {
      validate: {
        counterFormat: (value) =>
          floatCounterRegex.test(value as string) || (t("payments.must_follow_counter_format") as string),
      },
    },
  });

  useEffect(() => {
    if (responseError) {
      setError(name, { type: "backend", message: responseError });
    }
  }, [name, responseError, setError]);

  useEffect(() => {
    if (defaultValue && !isCalculateLoading) {
      setValue(name, formatCounterModel(defaultValue));
      setCounterViewValue(formatCounterView(defaultValue));
    }
  }, [defaultValue, name, setValue, isCalculateLoading]);

  const handleCounterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // matches x ; x. ; x.y ; x.yz
    if (isFloat) {
      if (!floatCounterRegex.test(value)) {
        if (formatCounterView(value).length === 1) {
          setError(name, {
            type: "manual",
            message: t("payments.must_follow_counter_format"),
          });
        }
        return;
      } else {
        setCounterViewValue(value);
        setValue(name, formatCounterModel(value), { shouldValidate: true });
      }
    } else {
      if (!counterRegex.test(value)) {
        if (formatCounterView(value).length === 1) {
          setError(name, {
            type: "manual",
            message: t("payments.must_follow_counter_format"),
          });
        }
        return;
      } else {
        setCounterViewValue(value);
        setValue(name, formatCounterModel(value), { shouldValidate: true });
      }
    }
  };

  const handleCounterBlur = () => {
    setCounterViewValue((prevState) => {
      if (prevState === "") {
        return prevState;
      }
      setValue(name, formatCounterModel(prevState), { shouldValidate: true, shouldTouch: true });
      const updatedCounter = formatCounterView(prevState);
      return updatedCounter;
    });
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Input
        isAMT
        sx={{ width: "100%" }}
        {...fieldProps}
        label={label}
        value={counterViewValue}
        inputVal={counterViewValue}
        readOnly={readOnly}
        disabled={disabled}
        {...register(fieldName as Path<T>)}
        onChange={handleCounterChange}
        onBlur={(e: React.FocusEvent) => {
          e.persist();
          onBlur();
          handleCounterBlur();
        }}
        onClick={(e) => {
          if ((e.target as HTMLInputElement).value === "0.00") {
            setCounterViewValue("");
          }
        }}
        error={error?.message || undefined}
        tooltipMessage={tooltipMessage}
      />
    </Box>
  );
}
